/**
 * AuthProvider - Authentication Context for Reconflux
 *
 * Manages Supabase authentication state and provides auth methods
 * throughout the application using React 19 features.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react'
import type { User, Session, AuthError } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { detectLocale } from '../i18n/detect'

// =============================================================================
// Types
// =============================================================================

interface AuthState {
  user: User | null
  session: Session | null
  isLoading: boolean
  isAuthenticated: boolean
  // True if the signed-in user is a platform moderator (can edit shared global
  // vendors). Defaults to false until resolved / on error (issue #53).
  isPlatformAdmin: boolean
}

interface AuthContextType extends AuthState {
  // Auth methods
  signUp: (email: string, password: string) => Promise<{ error: AuthError | null }>
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>
  signOut: () => Promise<{ error: AuthError | null }>
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>
  updatePassword: (password: string) => Promise<{ error: AuthError | null }>
}

// =============================================================================
// Context
// =============================================================================

const AuthContext = createContext<AuthContextType | null>(null)

// =============================================================================
// Provider
// =============================================================================

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    isLoading: true,
    isAuthenticated: false,
    isPlatformAdmin: false,
  })

  // Initialize auth state
  useEffect(() => {
    // Get initial session
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession()

        if (error) {
          console.error('Error getting session:', error)
        }

        setState(prev => ({
          ...prev,
          user: session?.user ?? null,
          session: session,
          isLoading: false,
          isAuthenticated: !!session?.user,
        }))
      } catch (error) {
        console.error('Auth initialization error:', error)
        setState(prev => ({ ...prev, isLoading: false }))
      }
    }

    initializeAuth()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event)

        // Only update state if auth status actually changed (not just token refresh)
        // This prevents unnecessary re-renders and data loss in forms
        setState(prev => {
          const newIsAuthenticated = !!session?.user
          const userChanged = prev.user?.id !== session?.user?.id

          // Skip update if nothing meaningful changed (token refresh, etc.)
          if (!userChanged && prev.isAuthenticated === newIsAuthenticated && !prev.isLoading) {
            return prev
          }

          return {
            ...prev,
            user: session?.user ?? null,
            session: session,
            isLoading: false,
            isAuthenticated: newIsAuthenticated,
          }
        })
      }
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  // Resolve platform-admin status whenever the signed-in user changes. Kept in a
  // dedicated effect (keyed on the user id) so it never races the auth-state
  // updater. Resolves to false on sign-out or any error, so a failed check never
  // grants moderation access (issue #53).
  const userId = state.user?.id
  const storedLocale = state.user?.user_metadata?.locale
  useEffect(() => {
    if (!userId) return
    const locale = detectLocale()
    if (storedLocale === locale) return
    // Keep server-triggered email aligned with this user's device preference.
    void supabase.auth.updateUser({ data: { locale } })
  }, [userId, storedLocale])

  useEffect(() => {
    if (!userId) {
      setState(prev => (prev.isPlatformAdmin ? { ...prev, isPlatformAdmin: false } : prev))
      return
    }

    let cancelled = false
    supabase
      .rpc('is_platform_admin')
      .then(({ data, error }) => {
        if (cancelled) return
        const isAdmin = !error && data === true
        setState(prev =>
          prev.isPlatformAdmin === isAdmin ? prev : { ...prev, isPlatformAdmin: isAdmin }
        )
      })

    return () => {
      cancelled = true
    }
  }, [userId])

  // Sign up with email/password
  const signUp = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    })
    return { error }
  }, [])

  // Sign in with email/password
  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { error }
  }, [])

  // Sign out
  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut()
    return { error }
  }, [])

  // Request password reset
  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    })
    return { error }
  }, [])

  // Update password (after reset)
  const updatePassword = useCallback(async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password })
    return { error }
  }, [])

  const value: AuthContextType = {
    ...state,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

// =============================================================================
// Hook
// =============================================================================

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}

// Export context for advanced use cases
export { AuthContext }
